import { useLayoutEffect, useRef, useState } from 'react';

import type { DocumentTemplate } from '@/entities/templates';

import type { DocumentHistory } from '../config';
import {
  applyNotePresetOperation,
  diffNotePresets,
  mergeNotePresetsChanges,
  patchNotePresets,
  type NoteOperation,
  type NotePresets,
  type NotePresetsPatch,
} from '../lib';

type PresetHistoryEntry = {
  before: NotePresetsPatch;
  after: NotePresetsPatch;
  // The operation's key and whether it typed text: typing into the text the
  // latest step typed into is one step with it.
  key: string;
  typing: boolean;
};

type PresetHistory = { undo: PresetHistoryEntry[]; redo: PresetHistoryEntry[] };

const EMPTY_PRESET_HISTORY: PresetHistory = { undo: [], redo: [] };

// The values, wording and line operations set before the recording, with one
// undo and redo of the whole note as on the note itself. The client keeps
// them, so a step is undone by patching them back. Undo and redo read the
// latest presets and steps, so a step made just before them, as typed text
// committed by the undo itself, is the step they act on.
export const usePresetHistory = ({
  template,
  presets,
  onChange,
}: {
  // The version the presets are set on: it says how a block takes "Other".
  template: DocumentTemplate;
  presets: NotePresets;
  onChange: (presets: NotePresets) => void;
}): DocumentHistory & {
  perform: (operation: NoteOperation) => void;
} => {
  const [history, setHistoryState] = useState(EMPTY_PRESET_HISTORY);
  const latest = useRef({ presets, history });

  useLayoutEffect(() => {
    latest.current = { ...latest.current, presets };
  }, [presets]);

  const update = (next: PresetHistory, nextPresets: NotePresets) => {
    latest.current = { presets: nextPresets, history: next };
    setHistoryState(next);
    onChange(nextPresets);
  };

  const perform = (operation: NoteOperation) => {
    const current = latest.current;
    const next = applyNotePresetOperation(current.presets, operation, template);
    const change = diffNotePresets(current.presets, next);
    if (!change) {
      return;
    }
    const top = current.history.undo.at(-1);
    const entry = {
      ...change,
      key: operation.key,
      typing: !!operation.typing,
    };
    const merges = !!top && top.typing && entry.typing && top.key === entry.key;
    update(
      {
        undo: merges
          ? [
              ...current.history.undo.slice(0, -1),
              { ...entry, ...mergeNotePresetsChanges(top, change) },
            ]
          : [...current.history.undo, entry],
        redo: [],
      },
      next,
    );
  };

  const move = (from: keyof PresetHistory) => {
    const current = latest.current;
    const entry = current.history[from].at(-1);
    if (!entry) {
      return;
    }
    const to = from === 'undo' ? 'redo' : 'undo';
    update(
      {
        ...current.history,
        [from]: current.history[from].filter((item) => item !== entry),
        [to]: [...current.history[to], entry],
      },
      patchNotePresets(
        current.presets,
        from === 'undo' ? entry.before : entry.after,
      ),
    );
  };

  return {
    perform,
    undo: () => move('undo'),
    redo: () => move('redo'),
    canUndo: history.undo.length > 0,
    canRedo: history.redo.length > 0,
  };
};
